import { BetaSchemaForm, ErrorBoundary, FormInstance } from '@ant-design/pro-components';
import type { FormSchema } from '@ant-design/pro-form/lib/components/SchemaForm';
import { Button, Form, Tooltip } from 'antd';
import { FormLayout } from 'antd/es/form/Form';
import { FC, PropsWithChildren, useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { FormValidationContext, useFormValidation } from '../../hooks';
import { useBtlApplicationInProgressById } from '../../queries';
import { config } from '../../utils/config';
import { BtlApplicationFormPageEnum, BtlFormSectionParam } from './types';
import { useBtlFormMappedRoutes } from './useBtlFormMappedRoutes';
import { ApplicationStatus } from 'api/btlApplicationForm/types';
import { useBtlMissingFields } from 'queries/btlApplications/useBtlMissingFields';

interface SubmitButtonProps {
  form?: FormInstance;
  isValid?: boolean;
}

// Note: this is used by the BtlBaseForm, and also as a standalone form mechanism for the Applicants and ProductSelector pages
export const FormFooter = ({
  form,
  captureFormData,
  loadFormData,
}: {
  form?: FormInstance<any>;
  captureFormData?: () => void;
  loadFormData?: () => void;
}) => {
  const navigate = useNavigate();
  const { formatMessage } = useIntl();
  const { isValid } = useContext(FormValidationContext) ?? {};

  const updateSectionInUrl = (section: BtlFormSectionParam) => {
    navigate({
      search: `?section=${section}`,
    });
  };

  const { id = '' } = useParams();
  const { data } = useBtlApplicationInProgressById(id);
  const { previousLabel, nextLabel, previousSection, isFormEditable, currentSection, nextSection } =
    useBtlFormMappedRoutes(data);
  const isBankOrSolicitorsSection =
    currentSection === BtlApplicationFormPageEnum.BANK_DETAILS ||
    currentSection === BtlApplicationFormPageEnum.SOLICITORS;
  const isDipAccepted = data?.status === ApplicationStatus.DIP_ACCEPTED;
  const showSkipButton = isBankOrSolicitorsSection && isDipAccepted && nextSection;
  const showForwardAction = form != null || nextSection != null;
  const submitButtonLabel = nextLabel ?? (form && !nextSection ? formatMessage({ id: 'common.saveContinue' }) : 'Next');

  const onSkip = () => {
    if (!nextSection) return;
    updateSectionInUrl(nextSection);
  };
  return (
    <div
      className={
        'flex justify-between z-10 w-full absolute bottom-0 right-0 backdrop-blur-md gap-8 p-2 mt-4 rounded-lg bg-gray-500/10 border border-solid border-white'
      }
    >
      <span className={'flex gap-2 '}>
        {previousSection &&
          data !== null && ( // NOTE: no data means it's a new application, so don't show the back button
            <Button
              size={'large'}
              key='back'
              onClick={() => (previousSection !== null ? updateSectionInUrl(previousSection) : null)}
            >
              {previousLabel ?? 'Back'}
            </Button>
          )}
        {/*<Button size={'large'} key='reset' disabled onClick={() => form?.resetFields()}>
          Reset
        </Button>*/}
      </span>
      {config.isDev && isFormEditable && (
        <span className={'flex gap-2 '}>
          {captureFormData && (
            <Button size={'large'} color={'orange'} variant={'filled'} onClick={() => captureFormData()}>
              Capture
            </Button>
          )}
          {loadFormData && (
            <Button size={'large'} onClick={() => loadFormData()}>
              Fill
            </Button>
          )}
        </span>
      )}
      {isFormEditable && showForwardAction && (
        <span className='flex gap-2'>
          {showSkipButton && <Button onClick={onSkip}>Skip</Button>}

          <SubmitButton form={form} isValid={form ? undefined : isValid}>
            {submitButtonLabel}
          </SubmitButton>
        </span>
      )}
    </div>
  );
};

const SubmitButton: FC<PropsWithChildren<SubmitButtonProps>> = ({ form, isValid, children }) => {
  const { id = '' } = useParams();
  const { data } = useBtlApplicationInProgressById(id);
  const { currentSection } = useBtlFormMappedRoutes(data);
  const { data: missingFieldsData } = useBtlMissingFields(id);
  const [submittable, setSubmittable] = useState<boolean>(false);
  const context = useFormValidation();

  const isMandatoryComplete = missingFieldsData?.isComplete;

  const values = Form.useWatch([], form);

  useEffect(() => {
    form
      ?.validateFields({ validateOnly: true })
      .then(() => setSubmittable(true))
      .catch(err => {
        const isValid = err.errorFields.length === 0;
        setSubmittable(isValid);
      });
  }, [form, values]);

  let submitButton = (
    <Button
      type='primary'
      size={'large'}
      htmlType='submit'
      /**
       * NOTE: the complexity here arises from the fact the FormFooter composing this component
       *        SOMETIMES doesn't participate in the Antd form validation, and instead overrides the
       *        isValid context, which means this becomes a little convoluted unfortunately :-/
       *
       * EXTRA NOTE: So there's 3 scnearios for determining this state:
       *    1. onChange AntD Form validation - submit button is only enabled if the form is valid. Any
       *       errors are immediately displayed to the user next to the input field.
       *
       *    2. onSubmit AntD Form validation - sometimes this immediate display of errors doesn't make
       *       sense, e.g. on the submit DIP page. We only want to display errors on click of the
       *       submit button.
       *
       *    3. Non AntD Form validation - when the footer is used for pages which aren't AntD forms
       *       then the isValid status to make the submit button enabled is provided externally.
       */
      disabled={
        (!submittable && context.validationTrigger == 'onChange' && form !== undefined) ||
        (!isValid && form === undefined) ||
        (data?.isAffordabilityRunRequired &&
          (currentSection === BtlApplicationFormPageEnum.SUBMIT_DIP ||
            currentSection === BtlApplicationFormPageEnum.ADDITIONAL_INFORMATION))
      }
      loading={context.isSubmitting}
      onClick={context.onSubmitForm}
    >
      {children}
    </Button>
  );

  if (!isMandatoryComplete && currentSection === BtlApplicationFormPageEnum.AFFORDABILITY_CHECK) {
    submitButton = (
      <Tooltip title='Please complete the preceding steps before continuing' placement='right' key={id}>
        {submitButton}
      </Tooltip>
    );
  }

  return submitButton;
};

type BtlBaseFormProps<T, ValueType = 'text'> = FormSchema<T, ValueType> & {
  isFormEditable?: boolean;
};

export const BtlBaseForm = <T, ValueType = 'text'>({ isFormEditable, ...props }: BtlBaseFormProps<T, ValueType>) => {
  const [layout] = useState<FormLayout>('vertical'); // TODO: remove this once settled on a layout
  const [form] = Form.useForm();

  // NOTE: just for quick form filling out during development
  const captureFormData = () => {
    const values = form.getFieldsValue(true); // Ensures deeply nested values are captured
    const serializedValues = JSON.stringify(values);
    localStorage.setItem(`${props.name}FormData`, serializedValues);
    console.log('Saved:', serializedValues);
  };

  const loadFormData = () => {
    const savedData = localStorage.getItem(`${props.name}FormData`);
    if (!savedData) return;
    const parsedData = JSON.parse(savedData);
    form.setFieldsValue(parsedData);
    console.log('Restored:', parsedData);
  };

  return (
    <ErrorBoundary>
      <BetaSchemaForm<T, ValueType>
        form={form}
        omitNil={false}
        autoFocusFirstInput={!!isFormEditable}
        className={'max-w-3xl'}
        layout={layout}
        size={'large'}
        labelCol={{
          span: layout === 'vertical' ? undefined : 5,
        }}
        wrapperCol={{
          span: layout === 'vertical' ? undefined : 21,
        }}
        rowProps={{
          gutter: [8, layout === 'inline' ? 12 : 0],
        }}
        grid={true}
        onFinish={async values => {
          // NOTE: You should probably be overriding onFinish in the parent form
          console.log('Values to submit:', values);
        }}
        submitter={{
          render: props => (
            <FormFooter form={props.form ?? form} loadFormData={loadFormData} captureFormData={captureFormData} />
          ),
        }}
        {...props}
      />
    </ErrorBoundary>
  );
};
